import type { FC, FormEvent, ReactNode } from 'react'

import type {
    RsvpErrors,
    RsvpField,
    RsvpPayload,
    RsvpResult
} from '@/types'

import { useEffect, useRef, useState } from 'react'

import {
    ArrowRight,
    CircleAlert,
    CircleCheck,
    Heart,
    LoaderCircle,
    Minus,
    PartyPopper,
    Plus
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import { RSVP_LIMITS, firstName, validateRsvp } from '@/lib/rsvp'
import CalendarButtons from '@/components/landing/calendar-buttons'
import { invitation } from '@/constants/invitation'

type Status = 'idle' | 'submitting' | 'success' | 'error'
type Attendance = 'yes' | 'no' | ''

type FormValues = {
    name: string
    email: string
    attendance: Attendance
    partySize: number
    dietary: string
    song: string
    message: string
    website: string
}

type SubmittedAnswer = {
    name: string
    attending: boolean
    updated: boolean
}

const INITIAL_VALUES: FormValues = {
    name: '',
    email: '',
    attendance: '',
    partySize: 1,
    dietary: '',
    song: '',
    message: '',
    website: ''
}

const DEFAULT_ERROR = "Oups, l’envoi n’a pas fonctionné. Vérifiez votre connexion et réessayez dans un instant."

const FIELD_BY_KEY: Partial<Record<keyof FormValues, RsvpField>> = {
    name: 'name',
    email: 'email',
    attendance: 'attending'
}

const FIELD_FOCUS_ORDER: Array<{ field: RsvpField, elementId: string }> = [
    { field: 'name', elementId: 'rsvp-name' },
    { field: 'email', elementId: 'rsvp-email' },
    { field: 'attending', elementId: 'rsvp-attendance-yes' }
]

const ATTENDANCE_OPTIONS = [
    {
        value: 'yes',
        label: "Oui, avec plaisir !",
        hint: "Je serai là pour faire la fête",
        icon: PartyPopper,
        tile: 'bg-festive-saffron text-foreground'
    },
    {
        value: 'no',
        label: "Non, je ne pourrai pas",
        hint: "Mais le cœur y sera",
        icon: Heart,
        tile: 'bg-festive-blush text-primary'
    }
] as const

const inputClasses = (invalid?: boolean): string =>
    cn(
        'block w-full rounded-xl border-2 bg-background px-4 py-3 text-base text-foreground transition-colors outline-none placeholder:text-muted-foreground/70 focus:border-primary focus:ring-[3px] focus:ring-ring/20',
        invalid ? 'border-destructive' : 'border-input hover:border-foreground/25'
    )

const describedBy = (id: string, error?: string, hasHint = false): string | undefined => {
    if (error) return `${id}-error`
    if (hasHint) return `${id}-hint`

    return undefined
}

interface FieldProps {
    id: string
    label: string
    required?: boolean
    error?: string
    hint?: string
    children: ReactNode
}

const Field: FC<FieldProps> = ({ id, label, required = false, error, hint, children }): ReactNode => {
    return (
        <div>
            <label htmlFor={id} className='flex items-baseline justify-between gap-3 text-sm font-semibold'>
                <span>
                    {label}
                    {required && <span aria-hidden='true' className='text-primary'> *</span>}
                </span>

                {!required && (
                    <span className='text-xs font-normal text-muted-foreground'>
                        Facultatif
                    </span>
                )}
            </label>

            <div className='mt-2'>
                {children}
            </div>

            {error && (
                <p id={`${id}-error`} className='mt-2 flex items-center gap-1.5 text-sm font-medium text-destructive'>
                    <CircleAlert className='size-4 shrink-0' aria-hidden='true' />
                    {error}
                </p>
            )}

            {!error && hint && (
                <p id={`${id}-hint`} className='mt-2 text-xs text-muted-foreground'>
                    {hint}
                </p>
            )}
        </div>
    )
}

const RsvpForm: FC = (): ReactNode => {
    const { honoree, date, time, maxPartySize } = invitation

    const [values, setValues] = useState<FormValues>(INITIAL_VALUES)
    const [errors, setErrors] = useState<RsvpErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [answer, setAnswer] = useState<SubmittedAnswer | null>(null)
    const successHeadingRef = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
        if (status === 'success') successHeadingRef.current?.focus()
    }, [status])

    const update = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
        setValues(current => ({ ...current, [key]: value }))

        const field = FIELD_BY_KEY[key]

        if (field) setErrors(current => (current[field] ? { ...current, [field]: undefined } : current))
    }

    const changePartySize = (delta: number) => {
        update('partySize', Math.min(Math.max(values.partySize + delta, 1), maxPartySize))
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const attending = values.attendance === '' ? null : values.attendance === 'yes'
        const nextErrors = validateRsvp({ name: values.name, email: values.email, attending })

        setErrors(nextErrors)

        const firstInvalid = FIELD_FOCUS_ORDER.find(({ field }) => nextErrors[field])

        if (firstInvalid) {
            document.getElementById(firstInvalid.elementId)?.focus()

            return
        }

        const payload: RsvpPayload = {
            name: values.name.trim(),
            email: values.email.trim(),
            attending,
            partySize: attending ? values.partySize : 0,
            dietary: attending ? values.dietary.trim() : '',
            song: attending ? values.song.trim() : '',
            message: values.message.trim(),
            website: values.website
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/rsvp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })

            const data = await response.json().catch(() => null) as RsvpResult | null

            if (!data || !data.ok) {
                if (data?.errors) setErrors(data.errors)

                setServerMessage(data?.message || DEFAULT_ERROR)
                setStatus('error')

                return
            }

            setAnswer({ name: firstName(payload.name), attending: Boolean(attending), updated: data.updated })
            setStatus('success')
        }

        catch {
            setServerMessage(DEFAULT_ERROR)
            setStatus('error')
        }
    }

    const handleEdit = () => {
        setStatus('idle')
        setAnswer(null)
        window.requestAnimationFrame(() => document.getElementById('rsvp-name')?.focus())
    }

    if (status === 'success' && answer) {
        const savedLabel = answer.updated ? "mise à jour" : "enregistrée"

        return (
            <div className='py-4 text-center animate-in fade-in zoom-in-95 duration-500 motion-reduce:animate-none'>
                <span
                    className={cn(
                        'mx-auto grid size-16 place-items-center rounded-full',
                        answer.attending ? 'bg-festive-saffron text-foreground' : 'bg-festive-blush text-primary'
                    )}
                >
                    {answer.attending
                        ? <PartyPopper className='size-7' aria-hidden='true' />
                        : <Heart className='size-7' aria-hidden='true' />}
                </span>

                <h3
                    ref={successHeadingRef}
                    tabIndex={-1}
                    className='mt-6 font-heading text-3xl leading-tight tracking-tight text-balance outline-none sm:text-4xl'
                >
                    {answer.attending ? `Merci ${answer.name}, c’est noté !` : `Merci ${answer.name} pour votre réponse`}
                </h3>

                <p className='mx-auto mt-4 max-w-md leading-relaxed text-muted-foreground text-pretty'>
                    {answer.attending
                        ? `Votre réponse a bien été ${savedLabel}. Rendez-vous le ${date.full.toLowerCase()} à ${time.start} !`
                        : `Votre réponse a bien été ${savedLabel}. Dommage que vous ne puissiez pas être là, on lèvera un verre à votre santé !`}
                </p>

                {answer.attending && (
                    <div className='mt-8'>
                        <p className='text-sm font-semibold'>
                            Ajoutez la soirée à votre agenda :
                        </p>

                        <CalendarButtons className='mt-3 justify-center' />
                    </div>
                )}

                <button
                    type='button'
                    onClick={handleEdit}
                    className='mt-8 rounded-sm text-sm font-semibold underline decoration-primary/40 underline-offset-4 outline-none hover:decoration-primary focus-visible:ring-[3px] focus-visible:ring-ring/40'
                >
                    Modifier ma réponse
                </button>
            </div>
        )
    }

    const attendingYes = values.attendance === 'yes'

    return (
        <form noValidate onSubmit={handleSubmit} aria-label={`Répondre à l’invitation de ${honoree}`} className='relative'>
            <div className='grid gap-6'>
                <div className='grid gap-6 sm:grid-cols-2'>
                    <Field id='rsvp-name' label="Prénom et nom" required error={errors.name}>
                        <input
                            id='rsvp-name'
                            name='name'
                            type='text'
                            autoComplete='name'
                            required
                            maxLength={RSVP_LIMITS.name}
                            placeholder="Ex. Léa Martin"
                            value={values.name}
                            onChange={event => update('name', event.target.value)}
                            aria-invalid={Boolean(errors.name)}
                            aria-describedby={describedBy('rsvp-name', errors.name)}
                            className={inputClasses(Boolean(errors.name))}
                        />
                    </Field>

                    <Field
                        id='rsvp-email'
                        label="Adresse e-mail"
                        required
                        error={errors.email}
                        hint="Pour modifier votre réponse plus tard."
                    >
                        <input
                            id='rsvp-email'
                            name='email'
                            type='email'
                            inputMode='email'
                            autoComplete='email'
                            required
                            maxLength={RSVP_LIMITS.email}
                            placeholder="prenom.nom@exemple.fr"
                            value={values.email}
                            onChange={event => update('email', event.target.value)}
                            aria-invalid={Boolean(errors.email)}
                            aria-describedby={describedBy('rsvp-email', errors.email, true)}
                            className={inputClasses(Boolean(errors.email))}
                        />
                    </Field>
                </div>

                <fieldset aria-describedby={errors.attending ? 'rsvp-attendance-error' : undefined}>
                    <legend className='text-sm font-semibold'>
                        Serez-vous présent·e ?
                        <span aria-hidden='true' className='text-primary'> *</span>
                    </legend>

                    <div className='mt-2 grid gap-3 sm:grid-cols-2'>
                        {ATTENDANCE_OPTIONS.map(option => {
                            const selected = values.attendance === option.value
                            const Icon = option.icon

                            return (
                                <label
                                    key={option.value}
                                    className={cn(
                                        'relative flex cursor-pointer items-center gap-3 rounded-2xl border-2 p-4 pr-10 transition-colors has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/40',
                                        selected ? 'border-primary bg-primary/5' : errors.attending ? 'border-destructive' : 'border-input hover:border-foreground/25'
                                    )}
                                >
                                    <input
                                        id={`rsvp-attendance-${option.value}`}
                                        type='radio'
                                        name='attendance'
                                        value={option.value}
                                        required
                                        checked={selected}
                                        onChange={() => update('attendance', option.value)}
                                        className='sr-only'
                                    />

                                    <span className={cn('grid size-10 shrink-0 place-items-center rounded-full', option.tile)}>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <span>
                                        <span className='block font-bold'>
                                            {option.label}
                                        </span>

                                        <span className='block text-sm text-muted-foreground'>
                                            {option.hint}
                                        </span>
                                    </span>

                                    {selected && (
                                        <CircleCheck className='absolute top-3 right-3 size-5 text-primary' aria-hidden='true' />
                                    )}
                                </label>
                            )
                        })}
                    </div>

                    {errors.attending && (
                        <p id='rsvp-attendance-error' className='mt-2 flex items-center gap-1.5 text-sm font-medium text-destructive'>
                            <CircleAlert className='size-4 shrink-0' aria-hidden='true' />
                            {errors.attending}
                        </p>
                    )}
                </fieldset>

                {attendingYes && (
                    <div className='grid gap-6 rounded-2xl bg-muted p-5 animate-in fade-in slide-in-from-top-2 duration-300 motion-reduce:animate-none sm:p-6'>
                        <div>
                            <p id='rsvp-party-label' className='text-sm font-semibold'>
                                Nombre de personnes (vous compris)
                            </p>

                            <div className='mt-2 flex flex-wrap items-center gap-4'>
                                <div className='inline-flex items-center gap-1 rounded-full border-2 border-input bg-background p-1'>
                                    <button
                                        type='button'
                                        onClick={() => changePartySize(-1)}
                                        disabled={values.partySize <= 1}
                                        aria-label='Retirer une personne'
                                        className='grid size-10 place-items-center rounded-full transition-colors outline-none hover:bg-foreground/5 focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-40'
                                    >
                                        <Minus className='size-4' aria-hidden='true' />
                                    </button>

                                    <span
                                        aria-live='polite'
                                        aria-atomic='true'
                                        aria-labelledby='rsvp-party-label'
                                        role='status'
                                        className='min-w-14 text-center font-heading text-2xl tabular-nums'
                                    >
                                        {values.partySize}
                                    </span>

                                    <button
                                        type='button'
                                        onClick={() => changePartySize(1)}
                                        disabled={values.partySize >= maxPartySize}
                                        aria-label='Ajouter une personne'
                                        className='grid size-10 place-items-center rounded-full transition-colors outline-none hover:bg-foreground/5 focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-40'
                                    >
                                        <Plus className='size-4' aria-hidden='true' />
                                    </button>
                                </div>

                                <p className='text-sm text-muted-foreground'>
                                    Jusqu’à {maxPartySize} personnes par réponse.
                                </p>
                            </div>
                        </div>

                        <Field id='rsvp-dietary' label="Allergies ou régime alimentaire">
                            <input
                                id='rsvp-dietary'
                                name='dietary'
                                type='text'
                                maxLength={RSVP_LIMITS.dietary}
                                placeholder="Végétarien, sans gluten, allergie aux fruits à coque…"
                                value={values.dietary}
                                onChange={event => update('dietary', event.target.value)}
                                className={inputClasses()}
                            />
                        </Field>

                        <Field id='rsvp-song' label="Une chanson pour vous faire danser">
                            <input
                                id='rsvp-song'
                                name='song'
                                type='text'
                                maxLength={RSVP_LIMITS.song}
                                placeholder="Ex. Dancing Queen, ABBA"
                                value={values.song}
                                onChange={event => update('song', event.target.value)}
                                className={inputClasses()}
                            />
                        </Field>
                    </div>
                )}

                <Field
                    id='rsvp-message'
                    label={`Un petit mot pour ${honoree}`}
                    hint={`${values.message.length} / ${RSVP_LIMITS.message} caractères`}
                >
                    <textarea
                        id='rsvp-message'
                        name='message'
                        rows={4}
                        maxLength={RSVP_LIMITS.message}
                        placeholder="Un souvenir, un mot doux, une anecdote à raconter…"
                        value={values.message}
                        onChange={event => update('message', event.target.value)}
                        aria-describedby={describedBy('rsvp-message', undefined, true)}
                        className={cn(inputClasses(), 'resize-y')}
                    />
                </Field>

                <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                    <label htmlFor='rsvp-website'>
                        Ne pas remplir ce champ
                    </label>

                    <input
                        id='rsvp-website'
                        name='website'
                        type='text'
                        tabIndex={-1}
                        autoComplete='off'
                        value={values.website}
                        onChange={event => update('website', event.target.value)}
                    />
                </div>

                {status === 'error' && serverMessage && (
                    <div role='alert' className='flex items-start gap-3 rounded-2xl bg-destructive/10 p-4 text-sm font-medium text-destructive'>
                        <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                        <p>{serverMessage}</p>
                    </div>
                )}

                <div>
                    <button
                        type='submit'
                        disabled={status === 'submitting'}
                        className={buttonStyles({ size: 'lg', className: 'w-full' })}
                    >
                        {status === 'submitting' ? (
                            <>
                                <LoaderCircle className='animate-spin' aria-hidden='true' />
                                Envoi en cours…
                            </>
                        ) : (
                            <>
                                Envoyer ma réponse
                                <ArrowRight aria-hidden='true' />
                            </>
                        )}
                    </button>

                    <p className='mt-3 text-center text-xs text-muted-foreground'>
                        Les champs marqués d’un <span className='text-primary'>*</span> sont obligatoires.
                    </p>
                </div>
            </div>
        </form>
    )
}

export default RsvpForm
